/**
 * PayPal Payment Button Component
 * Uses the legacy PayPal checkout.js SDK as required by the backend
 * Backend expects PAY-XXXXX format payment IDs from the legacy Payments API
 * 
 * SDK: https://www.paypalobjects.com/api/checkout.js
 */

import { useState, useEffect, useRef, useId } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import type { PaymentConfig } from '@/types/payment'

// Declare PayPal types for the legacy SDK
declare global {
  interface Window {
    paypal?: {
      Button: {
        render: (config: PayPalButtonConfig, selector: string) => void
      }
      FUNDING: {
        CREDIT: string
        CARD: string
      }
    }
    paypalResponse?: (type: 'success' | 'error', data: PayPalCallbackData) => void
    paypalEnable?: (actions: PayPalActions) => void
    paypalButtonClick?: (actions: PayPalActions) => void
  }
}

interface PayPalActions {
  enable: () => void
  disable: () => void
}

/** Minimal shapes for legacy checkout.js SDK callbacks (no official types). */
interface PayPalCallbackData {
  id?: string
  state?: string
  message?: string
  payer?: {
    payer_info?: {
      payer_id?: string
    }
  }
  [key: string]: unknown
}

interface PayPalPaymentActions {
  payment: {
    create: (options: unknown) => Promise<string>
    execute: () => Promise<PayPalCallbackData>
  }
  [key: string]: unknown
}

interface PayPalButtonConfig {
  env: 'sandbox' | 'production'
  style: {
    layout: string
    size: string
    shape: string
    color: string
  }
  commit: boolean
  client: {
    [key: string]: string
  }
  funding: {
    disallowed: string[]
  }
  validate?: (actions: PayPalActions) => void
  payment: (data: unknown, actions: PayPalPaymentActions) => Promise<string>
  onAuthorize: (data: unknown, actions: PayPalPaymentActions) => Promise<void>
  onError: (data: unknown) => void
  onCancel: (data: unknown, actions: unknown) => void
  onClick?: (data: unknown, actions: unknown) => void
}

export interface PayPalPaymentDetails {
  orderId: string
  paymentId: string // PayPal payment ID (PAY-...) - this is what backend expects as auth_code
  payerId?: string
  captureId?: string
  transactionId?: string
  status?: string
}

interface PayPalButtonProps {
  paymentConfig: PaymentConfig
  applicationFee: number
  paymentType?: 'APPLICATION_FEE' | 'CONTRACT' | 'DEFAULT_INVOICE'
  paymentAmount?: number // For contract/default_invoice payments (allows partial payments)
  applicantId: string | number
  onPaymentSuccess: (details: PayPalPaymentDetails) => void
  onPaymentError: (error: string) => void
  isLoading?: boolean
}

/**
 * PayPal Button Component using legacy checkout.js SDK
 * This matches the backend's expected payment flow
 */
export function PayPalButton({
  paymentConfig,
  applicationFee,
  paymentType = 'APPLICATION_FEE',
  paymentAmount,
  applicantId,
  onPaymentSuccess,
  onPaymentError,
  isLoading = false,
}: PayPalButtonProps) {
  const [error, setError] = useState<string | null>(null)
  const [isSDKLoading, setIsSDKLoading] = useState(true)
  const [isSDKLoaded, setIsSDKLoaded] = useState(false)
  const buttonContainerRef = useRef<HTMLDivElement>(null)
  const buttonRendered = useRef(false)
  const paypalActionsRef = useRef<PayPalActions | null>(null)
  const containerId = useId().replace(/:/g, '-') || 'paypal-btn'
  const buttonSelector = `#paypal-button-container-${containerId}`

  // Refs for callbacks so the render effect doesn't re-run on every parent re-render (e.g. countdown every second)
  const onPaymentSuccessRef = useRef(onPaymentSuccess)
  const onPaymentErrorRef = useRef(onPaymentError)
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: written in render so async PayPal SDK callbacks read fresh props; never read during render
  onPaymentSuccessRef.current = onPaymentSuccess
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: written in render so async PayPal SDK callbacks read fresh props; never read during render
  onPaymentErrorRef.current = onPaymentError

  const clientId = paymentConfig?.paypal?.client_id
  const mode = paymentConfig?.paypal?.mode || 'sandbox'
  const environmentType = mode === 'production' ? 'production' : 'sandbox'

  // Determine payment amount based on payment type
  const amountToUse = paymentAmount !== undefined ? paymentAmount : applicationFee

  // Format amount to 2 decimal places (safe even when invalid; the button render
  // is gated on isConfigValid below, so this value is only ever used when valid)
  const formattedAmount = (Number.isFinite(amountToUse) ? amountToUse : 0).toFixed(2)

  // PayPal is only usable when fully configured with a valid amount. Hooks must run
  // unconditionally (Rules of Hooks), so the effects below run every render but no-op
  // when this is false; the per-case early returns live after the hooks.
  const isConfigValid =
    Boolean(paymentConfig?.paypal?.status) && Boolean(clientId) && amountToUse > 0

  // Load the legacy PayPal checkout.js SDK
  useEffect(() => {
    if (!isConfigValid) return
    const loadPayPalSDK = () => {
      // Check if SDK is already loaded
      if (window.paypal) {
        setIsSDKLoading(false)
        setIsSDKLoaded(true)
        return
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script.paypal-checkout-js')
      if (existingScript) {
        // Wait for it to load
        const checkLoaded = setInterval(() => {
          if (window.paypal) {
            clearInterval(checkLoaded)
            setIsSDKLoading(false)
            setIsSDKLoaded(true)
          }
        }, 100)
        return
      }

      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.className = 'paypal-checkout-js'
      script.src = 'https://www.paypalobjects.com/api/checkout.js'
      script.async = true

      script.onload = () => {
        setIsSDKLoading(false)
        setIsSDKLoaded(true)
      }

      script.onerror = () => {
        setIsSDKLoading(false)
        setError('Failed to load PayPal SDK. Please refresh the page.')
        onPaymentError('Failed to load PayPal SDK')
      }

      document.head.appendChild(script)
    }

    loadPayPalSDK()
  }, [isConfigValid])

  // Render PayPal button when SDK is loaded
  useEffect(() => {
    if (!isSDKLoaded || !clientId || !window.paypal || !buttonContainerRef.current) {
      return
    }

    // Clear container so we don't duplicate buttons when effect re-runs (e.g. React StrictMode)
    const container = document.querySelector(buttonSelector)
    if (container) {
      container.innerHTML = ''
    }

    buttonRendered.current = true

    // Set up global callback functions (use refs so effect doesn't depend on callback identity)
    window.paypalResponse = (type: 'success' | 'error', data: PayPalCallbackData) => {
      const onSuccess = onPaymentSuccessRef.current
      const onError = onPaymentErrorRef.current
      if (type === 'success') {
        // Extract payment ID from response (legacy API: response.id is PAY-XXXXX)
        const paymentId = data?.id
        if (!paymentId) {
          const msg = 'PayPal did not return a payment ID. Please try again.'
          setError(msg)
          onError(msg)
          return
        }
        const payerId = data.payer?.payer_info?.payer_id

        const paymentDetails: PayPalPaymentDetails = {
          orderId: paymentId,
          paymentId,
          payerId,
          transactionId: paymentId,
          status: data.state || 'approved',
        }

        onSuccess(paymentDetails)
      } else {
        const errorMessage = data?.message || 'Payment was cancelled or failed'
        setError(errorMessage)
        onError(errorMessage)
      }
    }

    window.paypalEnable = (actions: PayPalActions) => {
      paypalActionsRef.current = actions
      // Enable by default
      if (actions && actions.enable) {
        actions.enable()
      }
    }

    window.paypalButtonClick = (_actions: PayPalActions) => {
      // Optional: validate before allowing payment
    }

    // Payment info to pass to PayPal for reference
    const paymentInfo = {
      applicant_id: applicantId,
      payment_type: paymentType,
    }

    try {
      // Render the PayPal button using legacy SDK
      window.paypal!.Button.render({
        env: environmentType,
        style: {
          layout: 'vertical',
          size: 'medium',
          shape: 'rect',
          color: 'gold',
        },
        commit: true,
        client: {
          [environmentType]: clientId,
        },
        funding: {
          disallowed: [
            window.paypal!.FUNDING.CREDIT,
            window.paypal!.FUNDING.CARD,
          ],
        },
        validate: (actions) => {
          paypalActionsRef.current = actions
          if (window.paypalEnable) {
            window.paypalEnable(actions)
          }
        },
        payment: (_data, actions) => {
          setError(null)
          
          return actions.payment.create({
            payment: {
              transactions: [{
                amount: {
                  total: formattedAmount,
                  currency: 'USD',
                },
                custom: JSON.stringify(paymentInfo),
              }],
            },
          })
        },
        onAuthorize: (_data, actions) => {
          return actions.payment.execute()
            .then((response: PayPalCallbackData) => {
              if (window.paypalResponse) {
                window.paypalResponse('success', response)
              }
            })
        },
        onError: (data) => {
          if (window.paypalResponse) {
            window.paypalResponse('error', data as PayPalCallbackData)
          }
        },
        onCancel: (_data, _actions) => {
          if (window.paypalResponse) {
            window.paypalResponse('error', { message: 'Payment was cancelled' })
          }
        },
        onClick: (_data, _actions) => {
          if (window.paypalButtonClick && paypalActionsRef.current) {
            window.paypalButtonClick(paypalActionsRef.current)
          }
        },
      }, buttonSelector)
    } catch {
      setError('Failed to render PayPal button. Please refresh the page.')
      onPaymentError('Failed to render PayPal button')
    }

    // Cleanup: reset so next effect run (e.g. React StrictMode) re-establishes callbacks and button
    return () => {
      buttonRendered.current = false
      delete window.paypalResponse
      delete window.paypalEnable
      delete window.paypalButtonClick
      const el = document.querySelector(buttonSelector)
      if (el) el.innerHTML = ''
    }
  }, [isSDKLoaded, clientId, environmentType, formattedAmount, applicantId, buttonSelector])

  // Validate PayPal configuration (early returns must follow all hooks above)
  if (!paymentConfig?.paypal?.status) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">PayPal payment is not available.</p>
        </div>
      </div>
    )
  }

  if (!clientId) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">PayPal Client ID is not configured.</p>
        </div>
      </div>
    )
  }

  if (!amountToUse || amountToUse <= 0) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">
            {paymentType === 'APPLICATION_FEE'
              ? 'Invalid application fee amount.'
              : 'Invalid payment amount. Please enter a valid amount.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {isSDKLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading PayPal...</span>
        </div>
      )}

      {/* PayPal Button Container - unique ID per instance for legacy SDK */}
      <div className="flex justify-center">
        <div 
          id={`paypal-button-container-${containerId}`}
          ref={buttonContainerRef}
          className={`min-h-[50px] ${isSDKLoading || isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Processing payment...</span>
        </div>
      )}
    </div>
  )
}
